// POS dizaynına uyğun təsdiq dialoqu — brauzerin `window.confirm()` əvəzi.
//
// Native confirm həm çirkin görünür (ağ sistem qutusu, "restaurant-management-front"
// başlığı), həm də tema/dil bilmir və tam ekran kiosk rejimində yad görünür.
//
// İstifadə (promise-əsaslı, `window.confirm` ilə demək olar ki eyni):
//
//   const { confirm, confirmDialog } = useConfirm();
//   ...
//   if (!(await confirm({ message: t('pos.removeSentConfirm'), variant: 'danger' }))) return;
//   ...
//   return (<> ...səhifə... {confirmDialog} </>);

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { AlertTriangle, HelpCircle } from 'lucide-react';
import { useI18n } from '../i18n/I18nContext';

export interface ConfirmOptions {
  /** Əsas sual. Tək məcburi sahə. */
  message: string;
  /** Başlıq — verilməsə "Əminsiniz?" işlədilir. */
  title?: string;
  /** Təsdiq düyməsinin mətni — verilməsə "Tamam". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` → qırmızı təsdiq düyməsi (silmə kimi geri dönməyən əməliyyatlar). */
  variant?: 'default' | 'danger';
}

interface DialogState extends ConfirmOptions {
  open: boolean;
}

const CLOSED: DialogState = { open: false, message: '' };

export function useConfirm(): {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: React.ReactNode;
} {
  const { t } = useI18n();
  const [state, setState] = useState<DialogState>(CLOSED);
  // Açıq dialoqun promise-ini burada saxlayırıq ki, cavab veriləndə resolve edək.
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  const confirmBtnRef = useRef<HTMLButtonElement | null>(null);

  const settle = useCallback((value: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(CLOSED);
    resolve?.(value);
  }, []);

  const confirm = useCallback((options: ConfirmOptions) => {
    // Əvvəlki dialoq hələ açıqdırsa onu "ləğv" kimi bağla (asılı qalmasın).
    resolverRef.current?.(false);
    setState({ ...options, open: true });
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Klaviatura: Esc → ləğv, Enter → təsdiq. Açılanda fokus təsdiq düyməsinə.
  useEffect(() => {
    if (!state.open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        settle(false);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        settle(true);
      }
    };
    window.addEventListener('keydown', onKey);
    const focusTimer = setTimeout(() => confirmBtnRef.current?.focus(), 30);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(focusTimer);
    };
  }, [state.open, settle]);

  // Komponent unmount olsa promise asılı qalmasın.
  useEffect(() => () => resolverRef.current?.(false), []);

  const isDanger = state.variant === 'danger';

  const confirmDialog = (
    <AnimatePresence>
      {state.open && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={() => settle(false)}
          role="presentation"
        >
          <motion.div
            className="w-full max-w-sm rounded-2xl bg-white dark:bg-[#111318] border border-gray-200 dark:border-white/10 shadow-2xl p-6"
            initial={{ scale: 0.94, y: 12, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 420, damping: 30, mass: 0.7 }}
            onClick={(e) => e.stopPropagation()}
            role="alertdialog"
            aria-modal="true"
          >
            <div className="flex flex-col items-center text-center gap-3">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  isDanger
                    ? 'bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400'
                    : 'bg-orange-50 dark:bg-orange-500/10 text-[var(--primary)]'
                }`}
              >
                {isDanger ? (
                  <AlertTriangle className="w-7 h-7" />
                ) : (
                  <HelpCircle className="w-7 h-7" />
                )}
              </div>

              <h3 className="text-lg font-black text-gray-900 dark:text-white">
                {state.title || t('common.confirmTitle')}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {state.message}
              </p>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                onClick={() => settle(false)}
                className="py-3 rounded-xl font-bold text-sm bg-gray-100 dark:bg-white/[0.06] text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/[0.1] transition-colors"
              >
                {state.cancelLabel || t('common.cancel')}
              </button>
              <button
                ref={confirmBtnRef}
                onClick={() => settle(true)}
                className={`py-3 rounded-xl font-bold text-sm text-white transition-all active:scale-95 shadow-lg ${
                  isDanger
                    ? 'bg-red-600 hover:bg-red-500 shadow-red-500/25'
                    : 'bg-[var(--primary)] hover:bg-orange-600 shadow-orange-500/25'
                }`}
              >
                {state.confirmLabel || t('common.ok')}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { confirm, confirmDialog };
}
